import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
} from '@angular/core';
import type { ECharts, EChartsOption } from 'echarts';
import { buildRadarOptions, RadarAxisInput, RadarTexts, wrapName } from './competency-radar.options';

/**
 * Netzdiagramm of a competency profile (`app-competency-profile`). Three
 * rings = three levels, the filled shape = the level per axis, the dashed
 * line = coverage. The list below the chart carries every value as text —
 * the chart is `role="img"` with a summary for screen readers.
 */
@Component({
  selector: 'app-competency-radar',
  templateUrl: './competency-radar.component.html',
  styleUrl: './competency-radar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompetencyRadarComponent implements OnChanges, OnDestroy {
  @Input() axes: RadarAxisInput[] = [];
  @Input() texts!: RadarTexts;
  @Input() showCoverage = true;
  @Input() ariaLabel = '';
  /** Click on an axis name → its key. */
  @Output() axisSelect = new EventEmitter<string>();

  options: EChartsOption = {};
  private chart: ECharts | null = null;
  private observer: ResizeObserver | null = null;
  private size = { width: 520, height: 380 };
  private readonly reducedMotion =
    typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngOnChanges(): void {
    this.render();
  }

  onChartInit(chart: ECharts): void {
    this.chart = chart;
    chart.on('click', (params: any) => {
      if (params?.componentType !== 'radar' || !params.name) return;
      const hit = this.axes.find((a) => wrapName(a.name) === params.name);
      if (hit) this.zone.run(() => this.axisSelect.emit(hit.key));
    });
    this.zone.runOutsideAngular(() => {
      this.observer = new ResizeObserver((entries) => {
        const box = entries[0]?.contentRect;
        if (!box || (Math.abs(box.width - this.size.width) < 2 && Math.abs(box.height - this.size.height) < 2)) {
          return;
        }
        this.size = { width: box.width, height: box.height };
        chart.resize();
        chart.setOption(this.build(), { replaceMerge: ['graphic'] });
      });
      this.observer.observe(this.host.nativeElement.querySelector('.radar-canvas') || this.host.nativeElement);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private render(): void {
    const el = this.host.nativeElement.querySelector('.radar-canvas') as HTMLElement | null;
    if (el?.clientWidth) this.size = { width: el.clientWidth, height: el.clientHeight };
    this.options = this.build();
  }

  private build(): EChartsOption {
    if (!this.texts || this.axes.length < 3) return {};
    return buildRadarOptions(this.axes, this.texts, this.size, {
      showCoverage: this.showCoverage,
      reducedMotion: this.reducedMotion,
    });
  }
}
